<template>
  <NotFoundBlock
    v-if="isNotFound"
    :api-url="experienceApiUrl"
    :sogu-url="soguServiceUrl"
  />
  <template v-else>
    <ArticleStaffEditBar :article-id="articleId" />
    <Header :geo="geo" />
    <ArticlePageSkeleton v-if="isLoading" />
    <template v-if="hasArticle">
      <SchemaOrg :data="articleData" />
      <article>
        <Wrap>
          <header class="post__head">
            <h1 v-html="articleTitleHtml" />
            <span
              class="post__tagline"
              v-html="articleTaglineHtml"
            />
            <AuthorAndDate
              :author="articleAuthor"
              :published-at="articlePublishedAt"
              :updated-at="articleUpdatedAt"
            />
            <div class="img-container">
              <div
                v-if="isUgc"
                class="articles-list-reader-experience"
              >
                Опыт читателя
              </div>
              <img
                v-if="articleCoverPhotoUrl"
                :src="articleCoverPhotoUrl"
                class="post__cover"
                :alt="titleWithoutSpecSymbols"
              />
            </div>
          </header>
          <TableOfContents :contents-tokens="contentsTokens">
            <PostSearch
              :selected="selectedCity"
              :total-experiences-count="totalExperiencesCount"
              widget-location="top"
            />
            <div
              class="post__intro-text"
              v-html="articleIntroHtml"
            />
          </TableOfContents>
        </Wrap>
        <Wrap>
          <div class="post-wrap">
            <Description
              :data="description"
              :article-title="titleWithoutSpecSymbols"
              :article-url="articleCanonicalUrl"
            />
          </div>
        </Wrap>
        <Wrap>
          <WriteBanner :article-id="articleId" />
          <Review
            :review="review"
            @send-reaction="sendReaction"
            @send-review="sendReview"
          />
        </Wrap>
        <div class="guides-container">
          <GuidesContainerAdapter
            v-if="hasRelatedGuides"
            :guides="guidesSectionGuides"
            title="Узнать больше помогут гиды"
            subtitle="Влюблены в место — и влюбляют других"
          />
        </div>
        <Wrap>
          <OtherPostList :posts="relatedArticles" />
          <ExperienceListingStandalone
            :experiences="experiencesWithFavorite"
            analytic-events-prefix="articles"
            :show-location="true"
            :more-button="showMoreButton"
            :more-button-loading="isLoadingExperiences"
            more-button-text="Показать еще"
            :skeletons-count="6"
            @load-more="handleLoadMore"
            @card-click="handleCardClick"
            @set-favorite="handleSetFavorite"
            @unset-favorite="handleUnsetFavorite"
          />
          <SubscriptionBlock
            api-url=""
            :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
            page-type="listing"
          />
        </Wrap>
        <UiToast
          class="success-toast"
          icon-name="check_circle_icon"
          :open="isReviewSuccessToastOpen"
          @close="isReviewSuccessToastOpen = false"
        >
          <template #icon>
            <IconBase
              name="check_circle_icon"
              size="24"
            />
          </template>
          <template #content>Отзыв отправлен</template>
        </UiToast>
      </article>
    </template>
  </template>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { computed, onBeforeMount } from 'vue';
  import { useRoute } from 'vue-router';
  import { UiToast } from '@experience/core-toast-ui';
  import { NotFoundBlock } from '@experience/site-404-package';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import AuthorAndDate from './components/AuthorAndDate.vue';
  import TableOfContents from './components/TableOfContents.vue';
  import PostSearch from './components/PostSearch.vue';
  import Review from './components/Review.vue';
  import Header from './components/Header.vue';
  import OtherPostList from './components/OtherPostList.vue';
  import SchemaOrg from './components/SchemaOrg.vue';
  import GuidesContainerAdapter from './components/GuidesContainerAdapter.vue';
  import ArticlePageSkeleton from './components/ArticlePageSkeleton.vue';
  import Description from './components/Description/Description.vue';
  import WriteBanner from './components/WriteBanner.vue';
  import ArticleStaffEditBar from './components/ArticleStaffEditBar.vue';
  import Wrap from '@/shared/ui/Wrap.vue';
  import IconBase from '@/shared/ui/iconBase/IconBase.vue';
  import { MF_NAME } from '@/app/const';
  import ExperienceListingStandalone from '@/features/ExperienceListingStandalone.vue';
  import { useExperienceEntity } from '@/entities/experience/entity';
  import { useArticleHashScroll } from './composables/useArticleHashScroll';
  import { useArticlePageData } from './composables/useArticlePageData';
  import { useArticleReview } from './composables/useArticleReview';
  import { useArticleExperiences } from './composables/useArticleExperiences';
  import { trackEvent } from '@/services/analytics';

  const runtimeConfig = useRuntimeConfig()[MF_NAME];
  const { VITE_RUNTIME_SOGU_SERVICE_URL } = runtimeConfig;
  const soguServiceUrl = VITE_RUNTIME_SOGU_SERVICE_URL ?? '';
  const experienceApiUrl = runtimeConfig.VITE_RUNTIME_API_URL ?? import.meta.env.VITE_RUNTIME_API_URL ?? '';
  const route = useRoute();
  const experienceEntity = useExperienceEntity();

  const {
    data,
    isLoading,
    isNotFound,
    getArticle,
    isUgc,
    titleWithoutSpecSymbols,
    contentsTokens,
    articleAuthor,
    geo,
    selectedCity,
    descriptionTokens,
    guidesSectionGuides,
    relatedArticles,
    hasRelatedGuides,
    totalExperiencesCount,
  } = useArticlePageData(route);

  useArticleHashScroll(route, data);

  const { review, isReviewSuccessToastOpen, getReview, sendReaction, sendReview } = useArticleReview(data);

  const {
    experiencesWithFavorite,
    isLoadingExperiences,
    showMoreButton,
    handleLoadMore,
    handleSetFavorite,
    handleUnsetFavorite,
    handleCardClick,
  } = useArticleExperiences(data);

  const hasArticle = computed(() => data.value !== null);
  const articleData = computed(() => data.value ?? {});

  const articleDetails = computed(() => data.value?.article_details);

  const articleId = computed(() => articleDetails.value?.id);

  const articleTitleHtml = computed(() => articleDetails.value?.title ?? '');

  const articleTaglineHtml = computed(() => articleDetails.value?.tagline ?? '');

  const articlePublishedAt = computed(() => articleDetails.value?.published_at);

  const articleUpdatedAt = computed(() => articleDetails.value?.updated_at);

  const articleCoverPhotoUrl = computed(() => articleDetails.value?.main_photo?.scaled);

  const articleIntroHtml = computed(() => articleDetails.value?.intro ?? '');

  const articleCanonicalUrl = computed(() => data.value?.seo?.canonical_url);

  const description = computed(() => ({
    id: articleId.value ?? 0,
    blocks: descriptionTokens.value,
  }));

  useHead(() => {
    if (isNotFound.value) {
      return {
        title: 'Страница не найдена',
        meta: [{ name: 'robots', content: 'noindex' }],
      };
    }

    const seo = data.value?.seo;
    const pageUrl = seo?.canonical_url || window.location.href;
    const ogImageUrl = articleCoverPhotoUrl.value;

    const meta: Array<{ name?: string; property?: string; content: string }> = [
      { name: 'robots', content: 'max-image-preview:large' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: pageUrl },
    ];

    if (seo?.description) {
      meta.push({ name: 'description', content: seo.description });
      meta.push({ property: 'og:description', content: seo.description });
    }

    if (seo?.title) {
      meta.push({ property: 'og:title', content: seo.title });
    }

    if (ogImageUrl) {
      meta.push({ property: 'og:image', content: ogImageUrl });
      meta.push({ property: 'og:image:width', content: '1220' });
      meta.push({ property: 'og:image:height', content: '600' });
      meta.push({ name: 'twitter:card', content: 'summary_large_image' });
      meta.push({ name: 'twitter:image', content: ogImageUrl });
    }

    return {
      title: seo?.title,
      link: seo?.canonical_url
        ? [{ rel: 'canonical', href: seo.canonical_url }]
        : [{ rel: 'canonical', href: pageUrl }],
      meta,
    };
  });

  onBeforeMount(async () => {
    await getArticle();
    if (isNotFound.value) {
      return;
    }
    getReview();
    experienceEntity.loadFavorites();
    trackEvent('journal:article.pageview', { slug: String(route.params.slug) });
  });
</script>

<style lang="scss" scoped>
  @import '@/app/styles/post_page';

  .post__cover {
    display: block;
    width: 100%;
    margin: var(--alias-space-32) 0 0;

    @include tablet {
      margin-top: var(--alias-space-24);
    }

    @include mobile {
      margin-top: var(--alias-space-20);
    }
  }

  .guides-container {
    margin-top: 80px;

    @include tablet {
      margin-top: 56px;
    }

    @include mobile {
      margin-top: 48px;
    }
  }

  .img-container {
    position: relative;
  }

  .articles-list-reader-experience {
    position: absolute;
    top: 0;
    left: 0;
    padding: 8px 24px;
    border-radius: 0 0 16px;
    background-color: var(--alias-color-background-accent-orange);
    color: var(--alias-color-text-inverse);

    @include base-300-300-semi;

    @include mobile {
      padding: 6px 12px;

      @include base-200-200-medium;
    }
  }
</style>
